<template>
  <div
    class="modal"
    :class="{ 'is-active': value }">

    <div class="modal-background"
      @click="close()">
    </div>
    
    <div class="modal-content">
      <div class="modal-card">

        <header class="modal-card-head">
          <p class="modal-card-title">Manage Fabrication Types</p>
          <button
            class="delete"
            aria-label="close"
            @click="close(true)">
          </button>
        </header>

        <div class="modal-card-body">

          <article 
            v-if="!typesLoading && fabricationTypes.length == 0" 
            class="message is-info is-fullwidth mt-3">
            <div class="message-body">
              There are no fabrication types.
            </div>
          </article>

          <table 
            class="table is-fullwidth is-hoverable">

            <thead>
              <tr>
                <th 
                  v-for="(header, headerIndex) in headers" 
                  :key="headerIndex">
                  {{ header.label }}
                </th>
                <th></th>
              </tr>
            </thead>

            <tfoot>
              <tr>
                <td :colspan="headers.length + 1">
                  <div class="centered">
                    <button
                      v-if="!adding"
                      @click="adding = true" 
                      class="button is-primary is-small">
                      Add a new fabrication type
                    </button>
                  </div>
                </td>
              </tr>
            </tfoot>

            <tbody>
              <tr
                class="v-centered"  
                v-for="type in fabricationTypes" 
                :key="type.id">

                <template v-if="editingId != type.id">
                  <td>
                    {{ type.fabrication_type_name }}
                  </td>
                  <td>
                    {{ type.fabrication_type_desc }}
                  </td>
                </template>

                <template v-else>
                  <td>
                    <v-text-field 
                      required
                      placeholder="Enter Fabrication Type Name"
                      v-model="type.fabrication_type_name">
                    </v-text-field>
                  </td>
                  <td>
                    <v-text-field 
                      placeholder="Enter Fabrication Type Description"
                      v-model="type.fabrication_type_desc">
                    </v-text-field>
                  </td>
                </template>
                <td>
                  <div class="buttons buttons-right">
                    <template v-if="editingId != type.id">
                      <button
                        @click="editingId = type.id" 
                        class="button is-small">
                        <span class="icon">
                          <i class="fas fa-edit has-text-grey"></i>
                        </span>
                      </button>
                      <button
                        @click="launchConfirmDelete(type.id)" 
                        class="button is-small">
                        <span class="icon">
                          <i class="fas fa-trash has-text-grey"></i>
                        </span>
                      </button>
                    </template>
                    <template v-else>
                      <button
                        @click="update(type)" 
                        class="button is-primary is-small">
                        <span class="icon">
                          <i class="fas fa-save"></i>
                        </span>
                      </button>
                      <button
                        @click="editingId = null" 
                        class="button is-small">
                        <span class="icon">
                          <i class="fas fa-times has-text-grey"></i>
                        </span>
                      </button>
                    </template>
                  </div>
                </td>
              </tr>

              <tr class="v-centered" v-if="adding">
                <td>
                  <v-text-field 
                    required
                    placeholder="Enter Fabrication Type Name"
                    v-model="type.fabrication_type_name">
                  </v-text-field>
                </td>
                <td>
                  <v-text-field 
                    placeholder="Enter Fabrication Type Description"
                    v-model="type.fabrication_type_desc">
                  </v-text-field>
                </td>
                <td>
                  <div class="buttons buttons-right">
                    <button
                      @click="add" 
                      class="button is-primary is-small">
                      <span class="icon">
                        <i class="fas fa-save"></i>
                      </span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </div>

    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmedCb ? deleteConfirmedCb() : null"
      @cancel="confirmDelete.state = false"/>

  </div>
</template>

<script>
import VTextField from '@/components/base/VTextField'
import VConfirmModal from '@/components/base/VConfirmModal'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  props: {
    value: {
      type: Boolean
    }
  },
  components: { VTextField, VConfirmModal },
  data() {
    return {
      headers: [
        { label: 'Name' },
        { label: 'Description' }
      ],
      type: {
        fabrication_type_name: '',
        fabrication_type_desc: null
      },
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this fabrication type?',
        type: 'danger'
      },
      deleteConfirmedCb: null,
      valid: false,
      adding: false,
      editingId: null
    }
  },
  computed: {
    ...mapState("fabrications", [
      "fabricationTypes", "typesLoading"
    ])
  },
  watch: {
    value(v) {
      if (!v) {
        this.adding = false;
      }
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("fabrications", [
      "getFabricationTypes","addFabricationType", "deleteFabricationType", 
      "updateFabricationType", "toggleAllSelected"
    ]),
    ...mapMutations("fabrications", [
      "selectAll", "deselectAll", "toggleSelected"
    ]),
    initialize() {
      if (this.fabricationTypes.length == 0) {
        this.getFabricationTypes();
      }
    },
    add() {
      this.addFabricationType(this.type).then(() => {
        this.adding = false;
        this.clearData();
      });
    },
    update(type) {
      this.updateFabricationType(
        { fabricationTypeId: type.id, data: type }
      ).then(() => {
        this.editingId = null;
      })
    },
    launchConfirmDelete(id) {
      this.deleteConfirmedCb = () => {
        this.deleteFabricationType(id);
        this.confirmDelete.state = false;
      }
      this.confirmDelete.message = 'Are you sure you want to delete this fabrication type?';
      this.confirmDelete.state = true;
    },
    close(clear=false) {
      this.$emit('input', false);
      if (clear) {
        this.clearData();
      }
    },
    clearData() {
      Object.assign(this.$data, this.$options.data.call(this));
    }
  }
}
</script>

<style scoped>
.buttons-right {
  justify-content: flex-end;
  flex-wrap: nowrap;
  align-items: center;
}

.table td {
  vertical-align: middle;
}

.table tfoot td .centered {
  display: flex;
  flex-direction: row;
  justify-content: center;
}

.modal-card {
  border-radius: 6px;
}
</style>
